import { useTranslation } from 'react-i18next';

function Pagination({ page, totalItems, itemsPerPage = 4, onPageChange }) {
  const { t } = useTranslation();
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  if (totalPages <= 1) return null;

  const getPageNumbers = () => {
    if (totalPages <= 6) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const siblingCount = 1;
    const leftSibling = Math.max(page - siblingCount, 1);
    const rightSibling = Math.min(page + siblingCount, totalPages);

    const showLeftDots = leftSibling > 3;
    const showRightDots = rightSibling < totalPages - 2;

    // Case 1: Near beginning (show 1 up to at least page + 1, dots, totalPages)
    if (!showLeftDots && showRightDots) {
      const endPage = Math.max(page + 1, 3);
      const range = Array.from({ length: endPage }, (_, i) => i + 1);
      return [...range, '...', totalPages];
    }

    // Case 2: Near end (show 1, dots, at least page - 1 down to totalPages)
    if (showLeftDots && !showRightDots) {
      const startPage = Math.min(page - 1, totalPages - 2);
      const range = [];
      for (let i = startPage; i <= totalPages; i++) {
        range.push(i);
      }
      return [1, '...', ...range];
    }

    // Case 3: In the middle (show 1, dots, page - 1, page, page + 1, dots, totalPages)
    return [1, '...', page - 1, page, page + 1, '...', totalPages];
  };

  const handlePageChange = newPage => {
    if (newPage !== page && newPage >= 1 && newPage <= totalPages) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      onPageChange(newPage);
    }
  };

  const pages = getPageNumbers();

  return (
    <nav aria-label="pagination" className="d-flex justify-content-center w-100">
      <ul className="pagination flex-nowrap justify-content-center mb-0">
        {page > 1 && (
          <li className="page-item">
            <a
              className="page-link px-2 px-sm-3"
              role="button"
              aria-label={t('pagination.prev', 'Prev')}
              onClick={() => handlePageChange(page - 1)}
            >
              <span className="d-none d-sm-inline">{t('pagination.prev', 'Prev')}</span>
              <span className="d-sm-none" aria-hidden="true">&lsaquo;</span>
            </a>
          </li>
        )}

        {pages.map((p, idx) =>
          p === '...' ? (
            <li
              key={`ellipsis-${idx}`}
              className="page-item"
              style={{ pointerEvents: 'none' }}
            >
              <span
                className="page-link px-2"
                style={{ color: '#fff', cursor: 'default' }}
              >
                ...
              </span>
            </li>
          ) : (
            <li key={p} className={`page-item ${page === p ? 'active' : ''}`}>
              <a
                className="page-link px-2 px-sm-3"
                role="button"
                onClick={() => handlePageChange(p)}
              >
                {p}
              </a>
            </li>
          )
        )}

        {page < totalPages && (
          <li className="page-item">
            <a
              className="page-link px-2 px-sm-3"
              role="button"
              aria-label={t('pagination.next', 'Next')}
              onClick={() => handlePageChange(page + 1)}
            >
              <span className="d-none d-sm-inline">{t('pagination.next', 'Next')}</span>
              <span className="d-sm-none" aria-hidden="true">&rsaquo;</span>
            </a>
          </li>
        )}
      </ul>
    </nav>
  );
}

export default Pagination;
